import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { clubsApi, championshipsApi, playersApi, marketApi } from '../../lib/api';
import { toast } from 'sonner';

// Domain hooks
import { useUsersActions } from './hooks/useUsersActions';
import { useClubsActions } from './hooks/useClubsActions';
import { useChampionshipsActions } from './hooks/useChampionshipsActions';
import { useMatchesActions } from './hooks/useMatchesActions';
import { useContentActions } from './hooks/useContentActions';
import { useMarketActions } from './hooks/useMarketActions';

export const useSuperAdminData = () => {
  const { t, i18n } = useTranslation();
  const isItalian = i18n.language === 'it';
  const { user, refreshUser } = useAuth();

  // Shared loading & tab
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stats');

  // Shared state (needed by multiple hooks)
  const [clubs, setClubs] = useState([]);

  // Players
  const [players, setPlayers] = useState([]);
  const [playerSearchQuery, setPlayerSearchQuery] = useState('');

  // Delete player dialog
  const [showDeletePlayerDialog, setShowDeletePlayerDialog] = useState(false);
  const [playerToDelete, setPlayerToDelete] = useState(null);

  // --- Domain hooks ---
  const market = useMarketActions({ isItalian });
  const champsHook = useChampionshipsActions({ isItalian });
  const matchesHook = useMatchesActions({ isItalian });
  const contentHook = useContentActions({ isItalian });

  // Users hook (defined first, owns users state, receives shared clubs)
  const usersHook = useUsersActions({
    isItalian, user, refreshUser, clubs, setClubs,
  });

  // Clubs hook (defined after users, receives users data + shared clubs)
  const clubsHook = useClubsActions({
    isItalian,
    users: usersHook.users,
    championships: champsHook.championships,
    clubs, setClubs,
    fetchUsers: usersHook.fetchUsers,
    refreshUser,
  });

  // --- Initial data fetch ---
  const fetchData = async () => {
    setLoading(true);
    try {
      const [clubsRes, champsRes, playersRes, marketRes] = await Promise.all([
        clubsApi.list({ include_logo: false }),
        championshipsApi.listAdmin(),
        playersApi.list(),
        marketApi.getStatus(),
      ]);
      await usersHook.fetchUsers(0, '');
      setClubs(clubsRes.data || []);
      champsHook.setChampionships(champsRes.data || []);
      setPlayers(playersRes.data || []);
      market.setMarketStatus(marketRes.data || { is_open: true });
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(isItalian ? 'Errore nel caricamento' : 'Error loading data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Debounced user search
  useEffect(() => {
    const timer = setTimeout(() => {
      usersHook.fetchUsers(0, usersHook.searchQuery);
    }, 400);
    return () => clearTimeout(timer);
  }, [usersHook.searchQuery]);

  // --- Helpers ---
  const getActionLabel = (action) => {
    const labels = {
      'user_login': { label: 'Login', color: 'bg-blue-500/20 text-blue-400' },
      'ban_user': { label: 'Ban Utente', color: 'bg-red-500/20 text-red-400' },
      'unban_user': { label: 'Unban Utente', color: 'bg-green-500/20 text-green-400' },
      'update_user_role': { label: 'Modifica Ruolo', color: 'bg-purple-500/20 text-purple-400' },
      'promote_to_admin': { label: 'Promozione Admin', color: 'bg-yellow-500/20 text-yellow-400' },
      'modify_personal_coins': { label: 'Coins Personali', color: 'bg-yellow-500/20 text-yellow-400' },
      'modify_club_coins': { label: 'Coins Club', color: 'bg-orange-500/20 text-orange-400' },
      'edit_club': { label: 'Modifica Club', color: 'bg-blue-500/20 text-blue-400' },
      'edit_club_logo': { label: 'Logo Club', color: 'bg-cyan-500/20 text-cyan-400' },
    };
    return labels[action] || { label: action, color: 'bg-slate-500/20 text-slate-400' };
  };

  const formatLogDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getBannerTypeLabel = (type) => {
    const labels = {
      info: { label: 'Info', color: 'bg-[#00F0FF]/20 text-[#00F0FF] border-[#00F0FF]/30' },
      warning: { label: isItalian ? 'Avviso' : 'Warning', color: 'bg-[#FFEA00]/20 text-[#FFEA00] border-[#FFEA00]/30' },
      urgent: { label: isItalian ? 'Urgente' : 'Urgent', color: 'bg-[#FF1744]/20 text-[#FF1744] border-[#FF1744]/30' },
      success: { label: isItalian ? 'Successo' : 'Success', color: 'bg-[#00E676]/20 text-[#00E676] border-[#00E676]/30' },
      promo: { label: 'Promo', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' }
    };
    return labels[type] || labels.info;
  };

  const getRoleBadge = (role) => {
    const colors = {
      super_admin: 'bg-[#FFEA00]/20 text-[#FFEA00] border-[#FFEA00]/30',
      admin: 'bg-[#D500F9]/20 text-[#D500F9] border-[#D500F9]/30',
      captain: 'bg-[#00F0FF]/20 text-[#00F0FF] border-[#00F0FF]/30',
      player: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
    };
    const labels = {
      super_admin: 'Super Admin', admin: 'Admin',
      captain: isItalian ? 'Capitano' : 'Captain',
      player: isItalian ? 'Giocatore' : 'Player',
    };
    return (
      <span className={`px-2 py-1 text-xs rounded border ${colors[role] || colors.player}`}>
        {labels[role] || role}
      </span>
    );
  };

  const stats = {
    totalUsers: usersHook.usersTotal,
    superAdmins: usersHook.roleCounts.super_admin ?? usersHook.users.filter(u => u.role === 'super_admin').length,
    admins: usersHook.roleCounts.admin ?? usersHook.users.filter(u => u.role === 'admin').length,
    captains: usersHook.roleCounts.captain ?? usersHook.users.filter(u => u.role === 'captain').length,
    players: usersHook.roleCounts.player ?? usersHook.users.filter(u => u.role === 'player').length,
    totalClubs: clubs.length,
    totalPlayers: players.length,
    totalChampionships: champsHook.championships.length,
    activeChampionships: champsHook.championships.filter(c => c.status === 'in_progress').length,
  };

  const handleDeletePlayer = (player) => { setPlayerToDelete(player); setShowDeletePlayerDialog(true); };
  const confirmDeletePlayer = async () => {
    if (!playerToDelete) return;
    try {
      await playersApi.delete(playerToDelete.player_id);
      setPlayers(prev => prev.filter(p => p.player_id !== playerToDelete.player_id));
      toast.success(isItalian ? `Player "${playerToDelete.name}" eliminato!` : `Player "${playerToDelete.name}" deleted!`);
      setShowDeletePlayerDialog(false); setPlayerToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore eliminazione' : 'Delete error'));
    }
  };

  // --- Return merged interface (identical shape to original) ---
  return {
    isItalian, user, USERS_PER_PAGE: usersHook.USERS_PER_PAGE, MATCHES_PAGE_SIZE: matchesHook.MATCHES_PAGE_SIZE,
    loading, activeTab, setActiveTab,
    players, playerSearchQuery, setPlayerSearchQuery,
    stats, fetchData,
    getActionLabel, formatLogDate, getBannerTypeLabel, getRoleBadge,
    showDeletePlayerDialog, setShowDeletePlayerDialog, playerToDelete, setPlayerToDelete,
    handleDeletePlayer, confirmDeletePlayer,

    // Users
    users: usersHook.users, usersTotal: usersHook.usersTotal, usersPage: usersHook.usersPage,
    filteredUsers: usersHook.filteredUsers,
    searchQuery: usersHook.searchQuery, setSearchQuery: usersHook.setSearchQuery,
    onPageChange: usersHook.onPageChange,
    handleUpdateRole: usersHook.handleUpdateRole, handleToggleBan: usersHook.handleToggleBan,
    selectedUserIds: usersHook.selectedUserIds, toggleSelectAll: usersHook.toggleSelectAll, toggleUserSelection: usersHook.toggleUserSelection,
    handleOpenSendCoins: usersHook.handleOpenSendCoins, handleOpenChangePassword: usersHook.handleOpenChangePassword,
    handleDeleteUser: usersHook.handleDeleteUser, setShowBulkDeleteDialog: usersHook.setShowBulkDeleteDialog,
    handleReleasePlayer: usersHook.handleReleasePlayer,
    showCoinsDialog: usersHook.showCoinsDialog, setShowCoinsDialog: usersHook.setShowCoinsDialog,
    selectedUser: usersHook.selectedUser, setSelectedUser: usersHook.setSelectedUser,
    coinsAmount: usersHook.coinsAmount, setCoinsAmount: usersHook.setCoinsAmount,
    coinsAction: usersHook.coinsAction, setCoinsAction: usersHook.setCoinsAction,
    coinsTarget: usersHook.coinsTarget, setCoinsTarget: usersHook.setCoinsTarget,
    handleModifyCoins: usersHook.handleModifyCoins,
    handleOpenModifyCoins: usersHook.handleOpenModifyCoins,
    showDeleteUserDialog: usersHook.showDeleteUserDialog, setShowDeleteUserDialog: usersHook.setShowDeleteUserDialog,
    userToDelete: usersHook.userToDelete, setUserToDelete: usersHook.setUserToDelete, confirmDeleteUser: usersHook.confirmDeleteUser,
    deleting: usersHook.deleting,
    showBanDialog: usersHook.showBanDialog, setShowBanDialog: usersHook.setShowBanDialog,
    userToBan: usersHook.userToBan, setUserToBan: usersHook.setUserToBan, confirmBanUser: usersHook.confirmBanUser,
    showSelfBanDialog: usersHook.showSelfBanDialog, setShowSelfBanDialog: usersHook.setShowSelfBanDialog, confirmSelfBan: usersHook.confirmSelfBan,
    showSendCoinsDialog: usersHook.showSendCoinsDialog, setShowSendCoinsDialog: usersHook.setShowSendCoinsDialog,
    coinsRecipient: usersHook.coinsRecipient, setCoinsRecipient: usersHook.setCoinsRecipient,
    coinsForm: usersHook.coinsForm, setCoinsForm: usersHook.setCoinsForm,
    sendingCoins: usersHook.sendingCoins, handleSendCoins: usersHook.handleSendCoins,
    showBulkDeleteDialog: usersHook.showBulkDeleteDialog, bulkDeleting: usersHook.bulkDeleting, confirmBulkDeleteUsers: usersHook.confirmBulkDeleteUsers,
    showChangePasswordDialog: usersHook.showChangePasswordDialog, setShowChangePasswordDialog: usersHook.setShowChangePasswordDialog,
    passwordTarget: usersHook.passwordTarget, setPasswordTarget: usersHook.setPasswordTarget,
    newPassword: usersHook.newPassword, setNewPassword: usersHook.setNewPassword,
    changingPassword: usersHook.changingPassword, confirmChangePassword: usersHook.confirmChangePassword,
    showCreateUserDialog: usersHook.showCreateUserDialog, setShowCreateUserDialog: usersHook.setShowCreateUserDialog,
    createUserForm: usersHook.createUserForm, setCreateUserForm: usersHook.setCreateUserForm,
    creatingUser: usersHook.creatingUser, handleCreateUser: usersHook.handleCreateUser,

    // Clubs
    clubs,
    clubSearchQuery: clubsHook.clubSearchQuery, setClubSearchQuery: clubsHook.setClubSearchQuery,
    handleOpenCreateClub: clubsHook.handleOpenCreateClub, handleViewRoster: clubsHook.handleViewRoster,
    handleEditClub: clubsHook.handleEditClub, handleDeleteClub: clubsHook.handleDeleteClub,
    handleOpenAddMember: clubsHook.handleOpenAddMember,
    showDeleteDialog: clubsHook.showDeleteDialog, setShowDeleteDialog: clubsHook.setShowDeleteDialog,
    clubToDelete: clubsHook.clubToDelete, setClubToDelete: clubsHook.setClubToDelete, confirmDeleteClub: clubsHook.confirmDeleteClub,
    showEditClubDialog: clubsHook.showEditClubDialog, setShowEditClubDialog: clubsHook.setShowEditClubDialog,
    editingClub: clubsHook.editingClub, setEditingClub: clubsHook.setEditingClub,
    editClubForm: clubsHook.editClubForm, setEditClubForm: clubsHook.setEditClubForm,
    savingClub: clubsHook.savingClub, handleSaveClub: clubsHook.handleSaveClub,
    handleUploadClubLogo: clubsHook.handleUploadClubLogo, editClubLogoRef: clubsHook.editClubLogoRef,
    showRosterDialog: clubsHook.showRosterDialog, setShowRosterDialog: clubsHook.setShowRosterDialog,
    selectedClubRoster: clubsHook.selectedClubRoster, clubMembers: clubsHook.clubMembers,
    loadingRoster: clubsHook.loadingRoster, handleAssignRole: clubsHook.handleAssignRole,
    showCreateClubDialog: clubsHook.showCreateClubDialog, setShowCreateClubDialog: clubsHook.setShowCreateClubDialog,
    createClubForm: clubsHook.createClubForm, setCreateClubForm: clubsHook.setCreateClubForm,
    creatingClub: clubsHook.creatingClub, availableCaptains: clubsHook.availableCaptains, handleCreateClub: clubsHook.handleCreateClub,
    showAddMemberDialog: clubsHook.showAddMemberDialog, setShowAddMemberDialog: clubsHook.setShowAddMemberDialog,
    addMemberClub: clubsHook.addMemberClub, addMemberUserId: clubsHook.addMemberUserId,
    setAddMemberUserId: clubsHook.setAddMemberUserId, addingMember: clubsHook.addingMember,
    usersWithoutClub: clubsHook.usersWithoutClub, handleAddMemberToClub: clubsHook.handleAddMemberToClub,

    // Championships
    championships: champsHook.championships,
    setShowCreateChampionshipDialog: champsHook.setShowCreateChampionshipDialog,
    handleGenerateSchedule: champsHook.handleGenerateSchedule, handleToggleRegistration: champsHook.handleToggleRegistration,
    handleSuspendChampionship: champsHook.handleSuspendChampionship, handleCompleteChampionship: champsHook.handleCompleteChampionship,
    handleDeleteChampionship: champsHook.handleDeleteChampionship, handleRemoveClubFromChampionship: champsHook.handleRemoveClubFromChampionship,
    expandedChampClubs: champsHook.expandedChampClubs, setExpandedChampClubs: champsHook.setExpandedChampClubs, removingClub: champsHook.removingClub,
    showCreateChampionshipDialog: champsHook.showCreateChampionshipDialog, championshipForm: champsHook.championshipForm, setChampionshipForm: champsHook.setChampionshipForm,
    creatingChampionship: champsHook.creatingChampionship, handleCreateChampionship: champsHook.handleCreateChampionship,
    showDeleteChampionshipDialog: champsHook.showDeleteChampionshipDialog, setShowDeleteChampionshipDialog: champsHook.setShowDeleteChampionshipDialog,
    championshipToDelete: champsHook.championshipToDelete, setChampionshipToDelete: champsHook.setChampionshipToDelete, confirmDeleteChampionship: champsHook.confirmDeleteChampionship,

    // Matches
    matches: matchesHook.matches, matchesTotal: matchesHook.matchesTotal,
    selectedChampForMatches: matchesHook.selectedChampForMatches, setSelectedChampForMatches: matchesHook.setSelectedChampForMatches,
    fetchMatchesForChampionship: matchesHook.fetchMatchesForChampionship, setMatches: matchesHook.setMatches,
    loadingMatches: matchesHook.loadingMatches, loadingMoreMatches: matchesHook.loadingMoreMatches,
    editingMatch: matchesHook.editingMatch, setEditingMatch: matchesHook.setEditingMatch,
    matchDateInput: matchesHook.matchDateInput, setMatchDateInput: matchesHook.setMatchDateInput,
    handleUpdateMatchDate: matchesHook.handleUpdateMatchDate, handleCancelMatchReport: matchesHook.handleCancelMatchReport,

    // Market
    marketStatus: market.marketStatus, togglingMarket: market.togglingMarket, handleToggleMarket: market.handleToggleMarket,
    showScheduleModal: market.showScheduleModal, setShowScheduleModal: market.setShowScheduleModal,
    scheduleOpenAt: market.scheduleOpenAt, setScheduleOpenAt: market.setScheduleOpenAt,
    scheduleCloseAt: market.scheduleCloseAt, setScheduleCloseAt: market.setScheduleCloseAt,
    savingSchedule: market.savingSchedule, handleSaveSchedule: market.handleSaveSchedule,

    // Bot
    botPhrases: contentHook.botPhrases, loadingPhrases: contentHook.loadingPhrases, fetchBotPhrases: contentHook.fetchBotPhrases,
    handleDeletePhrase: contentHook.handleDeletePhrase, handleSeedBotPhrases: contentHook.handleSeedBotPhrases,
    showAddPhraseDialog: contentHook.showAddPhraseDialog, setShowAddPhraseDialog: contentHook.setShowAddPhraseDialog,
    newPhrase: contentHook.newPhrase, setNewPhrase: contentHook.setNewPhrase, handleAddPhrase: contentHook.handleAddPhrase,

    // Logs
    activityLogs: contentHook.activityLogs, loadingLogs: contentHook.loadingLogs,
    logFilter: contentHook.logFilter, setLogFilter: contentHook.setLogFilter, actionTypes: contentHook.actionTypes,
    fetchActivityLogs: contentHook.fetchActivityLogs, handleClearOldLogs: contentHook.handleClearOldLogs, handleClearAllLogs: contentHook.handleClearAllLogs,

    // Banners
    banners: contentHook.banners, loadingBanners: contentHook.loadingBanners, fetchBanners: contentHook.fetchBanners,
    handleOpenBannerDialog: contentHook.handleOpenBannerDialog, handleToggleBanner: contentHook.handleToggleBanner, handleDeleteBanner: contentHook.handleDeleteBanner,
    showBannerDialog: contentHook.showBannerDialog, setShowBannerDialog: contentHook.setShowBannerDialog,
    editingBanner: contentHook.editingBanner, setEditingBanner: contentHook.setEditingBanner,
    bannerForm: contentHook.bannerForm, setBannerForm: contentHook.setBannerForm, handleSaveBanner: contentHook.handleSaveBanner,
    getBannerTypeLabel,
  };
};
